import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';

export const liveMatchId = (match) => {
  if (!match || typeof match !== 'object') return null;
  return match._id || match.id || match.match_id || null;
};

/** Alias used by Match Info deep links. */
export const matchIdFrom = liveMatchId;

/**
 * Match Info UI stays in the repo but is disconnected for now
 * (no route / click-throughs). Flip to true to re-enable.
 */
export const MATCH_INFO_ENABLED = false;

export const matchHref = (matchOrId) => {
  if (!MATCH_INFO_ENABLED) return null;
  const id =
    typeof matchOrId === 'string' || typeof matchOrId === 'number'
      ? matchOrId
      : liveMatchId(matchOrId);
  if (id == null || id === '') return null;
  return `/league/match/${id}`;
};

/**
 * Path to Match Info for a fixture row — works for played and TBD.
 * Prefers `/league/match/:id`; otherwise a preview URL with team ids.
 */
export const matchOpenPath = (match) => {
  if (!MATCH_INFO_ENABLED) return null;
  if (!match || typeof match !== 'object') return null;
  const byId = matchHref(match);
  if (byId) return byId;
  const homeId = match.home_team?._id || match.homeId;
  const awayId = match.away_team?._id || match.awayId;
  if (!homeId || !awayId) return null;
  const params = new URLSearchParams();
  params.set('home', String(homeId));
  params.set('away', String(awayId));
  const date = match.match_date || match.date;
  if (date) params.set('date', String(date));
  if (match.domestic_cup || Number(match.type) === 1) params.set('cup', '1');
  if (match.friendly) params.set('friendly', '1');
  if (match.training) params.set('training', '1');
  const homeName = match.home_team?.name || match.homeName;
  const awayName = match.away_team?.name || match.awayName;
  if (homeName) params.set('homeName', homeName);
  if (awayName) params.set('awayName', awayName);
  const homeSymbol = match.home_team?.symbol || match.homeSymbol;
  const awaySymbol = match.away_team?.symbol || match.awaySymbol;
  if (homeSymbol) params.set('homeSymbol', homeSymbol);
  if (awaySymbol) params.set('awaySymbol', awaySymbol);
  return `/league/match/preview?${params.toString()}`;
};

export const previewMatchFromSearch = (search) => {
  const params = new URLSearchParams(
    typeof search === 'string' ? search.replace(/^\?/, '') : ''
  );
  const homeId = params.get('home');
  const awayId = params.get('away');
  if (!homeId || !awayId) return null;
  return {
    home_team: {
      _id: homeId,
      name: params.get('homeName') || 'Home',
      symbol: params.get('homeSymbol') || null,
    },
    away_team: {
      _id: awayId,
      name: params.get('awayName') || 'Away',
      symbol: params.get('awaySymbol') || null,
    },
    match_date: params.get('date') || null,
    domestic_cup: params.get('cup') === '1' ? 1 : 0,
    friendly: params.get('friendly') === '1',
    training: params.get('training') === '1',
  };
};

export const isMatchLive = (match) => {
  if (!match) return false;
  if (match === true) return true;
  if (typeof match !== 'object') return false;
  return !isEmpty(match);
};

export const unwrapMatchPayload = (data) => {
  if (!data || typeof data !== 'object') return data;
  if (data.match && typeof data.match === 'object') return data.match;
  if (data.fixture && typeof data.fixture === 'object') return data.fixture;
  return data;
};

export const firstNumber = (...values) =>
  values.find((value) => isNumber(value));

export const matchClockMinute = (match) =>
  firstNumber(
    match?.minute,
    match?.current_minute,
    match?.currentMinute,
    match?.match_minute
  );

export const isHalfTime = (match) => {
  if (!match || typeof match !== 'object') return false;
  if (
    match.half_time === true ||
    match.isHalfTime === true ||
    match.ht === true ||
    match.halfTime === true
  ) {
    return true;
  }
  const status = String(
    match.status || match.match_status || match.period || match.phase || ''
  ).toLowerCase();
  return (
    status === 'ht' ||
    status === 'half' ||
    status.includes('half') ||
    status.includes('pause') ||
    status.includes('break')
  );
};

const eventMinuteMax = (events) => {
  let eventMax = 0;
  (events || []).forEach((event) => {
    const mins = Array.isArray(event?.minutes) ? event.minutes : [event?.minute];
    mins.forEach((raw) => {
      const n = Number(raw);
      if (Number.isFinite(n) && n > eventMax) eventMax = n;
    });
  });
  return eventMax;
};

/** Right edge of the pitch timeline — 90' or later if stoppage events exist. */
export const timelineEndMinute = (match, { events } = {}) => {
  const clock = matchClockMinute(match);
  const fromEvents = eventMinuteMax(events);
  return Math.max(90, isNumber(clock) ? clock : 0, fromEvents);
};

/** Progress 0–100 along the timeline; HT without a clock sits at 45'. */
export const liveProgressPercent = (match, { finished, events } = {}) => {
  const end = timelineEndMinute(match, { events });
  if (finished) return 100;
  const clock = matchClockMinute(match);
  if (isNumber(clock)) {
    return Math.max(0, Math.min(100, (Math.max(0, clock) / end) * 100));
  }
  if (isHalfTime(match)) return (45 / end) * 100;
  const eventMax = eventMinuteMax(events);
  if (eventMax > 0) {
    return Math.max(0, Math.min(100, (eventMax / end) * 100));
  }
  return 0;
};

export const liveMinuteLabel = (match, { withWord } = {}) => {
  const minute = matchClockMinute(match);
  if (isNumber(minute)) {
    return withWord ? `${minute}' minute` : `${minute}'`;
  }
  if (isHalfTime(match)) {
    return withWord ? 'Half time' : 'HT';
  }
  return withWord ? 'Live' : null;
};

const LIVE_CACHE_PREFIX = 'tol.matchInfo.live.';
const LIVE_CACHE_TTL_MS = 3 * 60 * 60 * 1000;

export const cacheLiveMatchSnapshot = (match, aliasIds = []) => {
  const id = liveMatchId(match);
  if (!match || typeof match !== 'object' || typeof sessionStorage === 'undefined') {
    return;
  }
  const ids = [id, ...aliasIds].filter(Boolean).map(String);
  if (!ids.length) return;
  const payload = JSON.stringify({ savedAt: Date.now(), match });
  try {
    ids.forEach((key) => {
      sessionStorage.setItem(LIVE_CACHE_PREFIX + key, payload);
    });
  } catch {
    /* private mode / quota */
  }
};

export const readCachedLiveMatch = (matchId) => {
  if (!matchId || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LIVE_CACHE_PREFIX + String(matchId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.match || typeof parsed.match !== 'object') return null;
    if (Date.now() - Number(parsed.savedAt || 0) > LIVE_CACHE_TTL_MS) {
      sessionStorage.removeItem(LIVE_CACHE_PREFIX + String(matchId));
      return null;
    }
    return parsed.match;
  } catch {
    return null;
  }
};

const goalsFromSide = (match, side) => {
  const stats = match?.[`${side}_team_statistics`];
  const team = match?.[`${side}_team`];
  const resultsTeam = match?.results?.[`${side}_team`];
  return firstNumber(
    Array.isArray(stats?.goals) ? stats.goals.length : null,
    match?.[`${side}_goals`],
    Array.isArray(team?.goals) ? team.goals.length : null,
    isNumber(team?.goals) ? team.goals : null,
    Array.isArray(resultsTeam?.goals) ? resultsTeam.goals.length : null,
    isNumber(resultsTeam?.goals) ? resultsTeam.goals : null
  );
};

export const liveScores = (match) => {
  const home = goalsFromSide(match, 'home');
  const away = goalsFromSide(match, 'away');
  return {
    home,
    away,
    hasScore: isNumber(home) && isNumber(away),
  };
};

export const isLiveMatchFinished = (data) => {
  if (data == null || typeof data !== 'object') return false;
  if (
    data.finished === true ||
    data.ended === true ||
    data.match_ended === true ||
    data.full_time === true
  ) {
    return true;
  }
  // Friendlies / past-match payloads often use numeric status 1 = done.
  if (data.status === 1 || data.status === '1') return true;
  const status = String(data.status || data.match_status || '').toLowerCase();
  if (
    status === 'finished' ||
    status === 'ended' ||
    status === 'complete' ||
    status === 'completed' ||
    status === 'done' ||
    status === 'ft' ||
    status === 'full_time' ||
    status === 'fulltime' ||
    status === 'full-time'
  ) {
    return true;
  }
  return false;
};

export const clearCachedLiveMatch = (matchId) => {
  if (!matchId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(LIVE_CACHE_PREFIX + String(matchId));
  } catch {
    /* ignore */
  }
};

export const mergeLiveMatch = (current, incoming) => {
  if (!incoming || typeof incoming !== 'object') return current;
  return {
    ...(current || {}),
    ...incoming,
    home_team: {
      ...(current?.home_team || {}),
      ...(incoming.home_team || {}),
    },
    away_team: {
      ...(current?.away_team || {}),
      ...(incoming.away_team || {}),
    },
  };
};
